import { CanAccess } from '@modules/common/components/CanAccess';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { FeedIcon } from '@modules/common/components/FeedIcon';
import { OpenInSearchButton } from '@modules/common/components/OpenInSearchButton';
import { LAST_5_YEARS } from '@modules/common/constants';
import {
  formatNumToStandardNotation,
  getWhereClauseForCohortsField,
} from '@modules/common/utils';
import { FileUploadInteractiveSVGIcon } from '@modules/integrationsV2/components/FileUploadInteractiveSVGIcon';
import { FileUploader } from '@modules/integrationsV2/components/FileUploader';
import { useFetchRecordCountForCohortGroup } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  ChatBubbleOutlineRounded,
  HelpOutlineRounded,
} from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, alpha } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationType,
  PermissionOperation,
  PermissionResource,
  TimeUnit,
} from 'generated/graphql';
import { getInstructionsUrlForChannel } from 'modules/ui/Settings/Integrations/utils/getInstructionsUrlForChannel';
import { useState } from 'react';
import { TimeRange } from 'v2/core/entities';

interface IntegrationSourceCohortGroupFileUploaderProps {
  selectedIntegrationSource: IntegrationSource;
}

export const IntegrationSourceCohortGroupFileUploader = ({
  selectedIntegrationSource,
}: IntegrationSourceCohortGroupFileUploaderProps) => {
  const [isUploadButtonHovered, setIsUploadButtonHovered] = useState(false);

  const isDisabled = selectedIntegrationSource.isDisabled;

  const cohortName = selectedIntegrationSource.namespace ?? '';

  const { recordCount, isLoading } = useFetchRecordCountForCohortGroup({
    cohortName,
    skip: !cohortName,
  });

  if (isLoading) {
    return <CircularLoader />;
  }

  const shouldRenderRecordCountBanner = !!recordCount;

  const renderMatchingRecordCountBanner = () => {
    if (!shouldRenderRecordCountBanner) {
      return null;
    }

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          borderRadius: 1,
          py: 0.8,
          px: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ChatBubbleOutlineRounded
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography variant="body2">
            {formatNumToStandardNotation(recordCount)} feedback records were
            found for this cohort
          </Typography>
        </Stack>
        <OpenInSearchButton
          size="small"
          startIcon={<FeedIcon />}
          variant="text"
          queryContainer={{
            DateRange: LAST_5_YEARS as TimeRange,
            WhereClause: getWhereClauseForCohortsField(cohortName),
            AggregatePeriod: TimeUnit.Week,
          }}
        />
      </Stack>
    );
  };

  return (
    <Stack>
      {renderMatchingRecordCountBanner()}
      {!shouldRenderRecordCountBanner && (
        <Stack
          pt={10}
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <Stack alignItems="center" justifyContent="center" spacing={3}>
            <Stack alignItems="center" justifyContent="center">
              <FileUploadInteractiveSVGIcon
                isHighlighted={isUploadButtonHovered}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                {isDisabled ? (
                  <Stack textAlign="center">
                    <Typography>
                      This cohort integration is disabled. Previously ingested
                      data is still available for analysis.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack textAlign="center" maxWidth={500}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="center"
                    >
                      <Typography>Upload file to your cohort.</Typography>
                      {!isDisabled && (
                        <IconButton
                          color="primary"
                          onClick={() => {
                            window.open(
                              getInstructionsUrlForChannel(
                                {
                                  type: IntegrationChannelType.FileUpload,
                                },
                                IntegrationType.Cohorts,
                              ),
                            );
                          }}
                        >
                          <HelpOutlineRounded />
                        </IconButton>
                      )}
                    </Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      After you upload a file, you'll begin to see matches in
                      feedback records within 60 mins. It might take up to 4
                      hours for all feedback matches to get updated with the
                      cohort.
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
            {selectedIntegrationSource.integrationID && !isDisabled && (
              <CanAccess
                operation={PermissionOperation.Write}
                resource={PermissionResource.Integration}
              >
                <Box
                  onMouseEnter={() => setIsUploadButtonHovered(true)}
                  onMouseLeave={() => setIsUploadButtonHovered(false)}
                >
                  <FileUploader
                    id={selectedIntegrationSource.integrationID}
                    integrationType={IntegrationType.Cohorts}
                    buttonProps={{
                      size: 'large',
                    }}
                  />
                </Box>
              </CanAccess>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
